import React, {useEffect, useState} from 'react';
import ModalDefault from '../modal-default';
import ChatbotBox from '../../layout/layout-components/AIChat/chatbot-box';
import Button from '../../button';
import ButtonIcon from '../../button/button-icon';
import Badge from '../../badge';
import Tooltip from '../../tooltip';
import {classNames} from '../../../util/util-helpers';
import {AI_DOCUMENT_PROCESSING_MESSAGES, torqueImageSets} from '../../../util/util-consts';
import DocumentPreview from './document-preview';
import TimeoutTimer from './timeout-timer';
import DocumentTextIcon from '@heroicons/react/24/outline/DocumentTextIcon';
import ClipboardDocumentListIcon from '@heroicons/react/24/outline/ClipboardDocumentListIcon';
import PencilSquareIcon from '@heroicons/react/24/outline/PencilSquareIcon';
import ExclamationTriangleIcon from '@heroicons/react/24/outline/ExclamationTriangleIcon';
import ChevronLeftIcon from '@heroicons/react/20/solid/ChevronLeftIcon';
import ChevronRightIcon from '@heroicons/react/20/solid/ChevronRightIcon';
import {resolvePageNumber, resolvePreviewKind, PREVIEW_KIND_PDF} from './preview-helpers';

const TIMEOUT_MS = 180000;

// Definite height for the dialog body, deliberately NOT the `h-dialog-body` utility.
// That utility is `max-h-[calc(100vh-12rem)] overflow-auto` — a max-height only — and the
// dialog panel above it is `inline-block … w-full` with auto height. A percentage height
// resolved against a containing block whose own height depends on its content computes to
// `auto`, which collapses the whole `h-full` chain below (split wrapper → panel → preview →
// its `flex-1 overflow-auto` scroll container). With no definite height the preview panel
// never overflows, so it never scrolls, ctrl-drag pan has nothing to move, and the modal
// body scrolls instead. Same 100vh-12rem budget, expressed as a real height.
const DIALOG_BODY_HEIGHT_CLASS = 'h-[calc(100vh-12rem)] overflow-auto';

export default function OcrSplitDialog(props) {
    const {
        show, title, file, isLoading, isError, onRetry, onClose,
        renderForm, renderSummary, onSave, saveLabel, saveDisabled,
        onCancel, cancelLabel, translate: translateProp, headerOptions,
        isBeta,
        // Replaces the built-in document pane. For sources DocumentPreview cannot render —
        // a .csv, say — the consumer supplies its own view of the file.
        renderPreview,
    } = props;

    const [isTimeout, setIsTimeout] = useState(() => false);
    const [isDocumentVisible, setIsDocumentVisible] = useState(() => true);
    const [isSummaryVisible, setIsSummaryVisible] = useState(() => true);
    const [isFormVisible, setIsFormVisible] = useState(() => true);

    const [randomTorqueSet] = useState(() => torqueImageSets[Math.floor(Math.random() * torqueImageSets.length)]);
    const [dataLoadingRandomIndex] = useState(() => Math.floor(Math.random() * AI_DOCUMENT_PROCESSING_MESSAGES.length));

    const [currentPage, setCurrentPage] = useState(() => 1);
    const [pagesNumber, setPagesNumber] = useState(() => 0);
    const [paginationInputValue, setPaginationInputValue] = useState(() => 1);

    // Every label this shell renders comes from the host app's translate. Degrade to the raw
    // key rather than throwing if a consumer forgets to pass one.
    const translate = typeof translateProp === 'function' ? translateProp : ((key) => key);

    const resolvedFile = Array.isArray(file) ? file[0] : file;
    const isPdf = resolvePreviewKind(resolvedFile) === PREVIEW_KIND_PDF;

    // A control with no handler behind it is worse than no control: modal-footer renders its
    // buttons purely on label truthiness and then calls the handler unguarded. So the presence
    // of the handler decides whether the label (and therefore the button) exists at all.
    const handleClose = onCancel ?? onClose;
    const hasSummary = typeof renderSummary === 'function';
    const hasForm = typeof renderForm === 'function';

    const showDocument = isDocumentVisible;
    const showSummary = hasSummary && isSummaryVisible;
    const showForm = hasForm && isFormVisible;

    // Single source of truth for how many panels are currently on screen.
    // Drives both the "can't hide the last panel" toggle-disabling rule and the width split below.
    const visiblePanelCount = [showDocument, showSummary, showForm].filter(Boolean).length;

    // Visual left-to-right order of the panels that are actually rendered. Used to decide which
    // panel is currently rightmost, so only panels with a following sibling draw a divider.
    const visiblePanels = [
        showDocument && 'document',
        showSummary && 'summary',
        showForm && 'form',
    ].filter(Boolean);

    function togglePanel(panel) {
        if (panel === 'document') {
            setIsDocumentVisible((prev) => !prev);
        } else if (panel === 'summary') {
            setIsSummaryVisible((prev) => !prev);
        } else if (panel === 'form') {
            setIsFormVisible((prev) => !prev);
        }
    }

    function isToggleDisabled(currentlyVisible) {
        return currentlyVisible && visiblePanelCount <= 1;
    }

    function panelWidthClass(isPanelDocument) {
        if (visiblePanelCount === 1) {
            return 'w-full';
        }

        if (visiblePanelCount === 2) {
            return 'w-1/2';
        }

        return isPanelDocument ? 'w-1/2' : 'w-1/4';
    }

    function footerButtonSegmentWidthClass() {
        if (!showDocument) {
            return visiblePanelCount > 0 ? 'w-full' : '';
        }
        return visiblePanelCount > 1 ? 'w-1/2' : '';
    }

    function hasTrailingDivider(panelKey) {
        return visiblePanels.indexOf(panelKey) < visiblePanels.length - 1;
    }

    // min-w-0 lets each column shrink below its content width: in a row flex the automatic
    // minimum size is the content width, so a zoomed-in PDF page would otherwise widen the
    // document panel and push the form off screen instead of scrolling inside its own panel.
    const panelBaseClass = 'h-full min-w-0';

    const panelToggles = (
        <div className="flex items-center gap-1">
            <ButtonIcon
                tooltip={translate('text.toggle_document_preview')}
                disabled={isToggleDisabled(isDocumentVisible)}
                onClick={() => togglePanel('document')}
            >
                <DocumentTextIcon className={classNames('w-5 h-5', isDocumentVisible ? 'text-primary' : 'text-tm-gray-600')}/>
            </ButtonIcon>

            {hasSummary && (
                <ButtonIcon
                    tooltip={translate('text.toggle_document_summary')}
                    disabled={isToggleDisabled(isSummaryVisible)}
                    onClick={() => togglePanel('summary')}
                >
                    <ClipboardDocumentListIcon className={classNames('w-5 h-5', isSummaryVisible ? 'text-primary' : 'text-tm-gray-600')}/>
                </ButtonIcon>
            )}

            {hasForm && (
                <ButtonIcon
                    tooltip={translate('text.toggle_form')}
                    disabled={isToggleDisabled(isFormVisible)}
                    onClick={() => togglePanel('form')}
                >
                    <PencilSquareIcon className={classNames('w-5 h-5', isFormVisible ? 'text-primary' : 'text-tm-gray-600')}/>
                </ButtonIcon>
            )}
        </div>
    );

    // Single entry point for every page change so the rendered page and the input box are
    // always written from the same absolute number and can never drift apart.
    function goToPage(page) {
        setCurrentPage(page);
        setPaginationInputValue(page);
    }

    function handlePaginationInputKeyDown(e) {
        if (e.key !== 'Enter') {
            return;
        }

        goToPage(resolvePageNumber(paginationInputValue, pagesNumber, currentPage));
    }

    function handlePaginationInputBlur() {
        setPaginationInputValue(resolvePageNumber(paginationInputValue, pagesNumber, currentPage));
    }

    const paginationControls = showDocument && isPdf && pagesNumber > 1 && (
        <div className="relative z-0 inline-flex -space-x-px">
            <button
                type="button"
                className="btn btn-outline-secondary border border-tm-gray-300 px-2 rounded-r-none z-10"
                disabled={currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
            >
                <span className="sr-only">{translate('text.previous_page')}</span>
                <ChevronLeftIcon className="w-5 h-5"/>
            </button>

            <input
                type="text"
                className="form-control rounded-none w-12 text-right px-1.5 relative focus:z-20"
                value={paginationInputValue}
                onChange={(e) => setPaginationInputValue(e.target.value)}
                onKeyDown={handlePaginationInputKeyDown}
                onBlur={handlePaginationInputBlur}
                onFocus={(e) => e.target.select()}
            />

            <input
                disabled={true}
                className="form-control rounded-none w-12 text-right px-1.5"
                value={translate('text.pagination_of', [pagesNumber])}
            />

            <button
                type="button"
                className="btn btn-outline-secondary border border-tm-gray-300 px-2 rounded-l-none"
                disabled={currentPage >= pagesNumber}
                onClick={() => goToPage(currentPage + 1)}
            >
                <span className="sr-only">{translate('text.next_page')}</span>
                <ChevronRightIcon className="w-5 h-5"/>
            </button>
        </div>
    );

    // A fresh processing cycle starts whenever the caller flips isLoading back on (e.g. after the
    // error card's Retry), reopens the dialog, or swaps in another document. Any of those must
    // clear a stale timeout flag left over from the previous cycle, so the banner doesn't reappear
    // immediately and so TimeoutTimer is allowed to re-arm — it early-returns while isTimeout is
    // true. isTimeout is deliberately NOT a dependency: mid-cycle the timer flips it to true while
    // isLoading/show/resolvedFile are all unchanged, so this effect does not re-run and the banner
    // stays up.
    useEffect(() => {
        if (isLoading || show) {
            setIsTimeout(false);
        }
    }, [isLoading, show, resolvedFile]);

    const betaTag = isBeta && (
        <Tooltip content={translate('text.beta_tooltip')}>
            <span className="ml-2 inline-flex">
                <Badge
                    type="custom"
                    addClass="bg-primary-transparent text-primary uppercase tracking-wide"
                    paddingClass="px-2 py-0.5"
                    textClass="text-xs font-semibold"
                >
                    {translate('text.beta')}
                </Badge>
            </span>
        </Tooltip>
    );

    return (
        <ModalDefault
            show={show}
            widthClass="max-w-screen"
            addClass={DIALOG_BODY_HEIGHT_CLASS}
            title={title}
            afterTitle={betaTag}
            options={(
                <div className="flex items-center gap-2">
                    {!isLoading && !isError && panelToggles}

                    {headerOptions}
                </div>
            )}
            onClose={handleClose}
            hideDialogFooter={true}
        >
            {isLoading && (
                <div className="flex flex-col items-center justify-center h-full gap-6 p-10">
                    <picture>
                        <source type="image/webp" srcSet={`${randomTorqueSet.webp1x} 1x, ${randomTorqueSet.webp2x} 2x`}/>

                        <source type="image/gif" srcSet={`${randomTorqueSet.gif1x} 1x, ${randomTorqueSet.gif2x} 2x`}/>

                        <img src={randomTorqueSet.gif1x} alt="" className="h-40 w-auto"/>
                    </picture>

                    <div className="max-w-[80ch] text-center text-tm-gray-900">
                        <ChatbotBox
                            fakeStreamSpeed={20}
                            streamContent={true}
                            content={AI_DOCUMENT_PROCESSING_MESSAGES[dataLoadingRandomIndex]}
                        />
                    </div>

                    <TimeoutTimer
                        time={TIMEOUT_MS}
                        isTimeout={isTimeout}
                        onTimeout={() => setIsTimeout(true)}
                    />

                    {isTimeout && (
                        <div className="text-base flex flex-wrap items-center bg-yellow-600/10 text-tm-gray-900 py-2 px-4 rounded-btn">
                            <span>{translate('text.ocr_processing_taking_longer')}</span>

                            {!!onRetry && (
                                <Button
                                    addClass="ml-4 rounded-btn"
                                    onClick={() => {
                                        setIsTimeout(false);
                                        onRetry();
                                    }}
                                >
                                    {translate('btn.retry')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {!isLoading && isError && (
                <div className="flex flex-col items-center justify-center h-full gap-4 p-10">
                    <ExclamationTriangleIcon className="w-12 h-12 text-yellow-600"/>

                    <span className="text-base text-tm-gray-900">{translate('text.OCR_processing_error')}</span>

                    {!!onRetry && (
                        <Button addClass="rounded-btn" onClick={() => onRetry()}>
                            {translate('btn.retry')}
                        </Button>
                    )}
                </div>
            )}

            {!isLoading && !isError && (
                <div className="h-full flex flex-col">
                    <div className="flex-1 min-h-0 flex">
                        {showDocument && (
                            <div className={classNames(panelBaseClass, hasTrailingDivider('document') && 'border-r border-tm-gray-200', panelWidthClass(true))}>
                                {renderPreview
                                    ? renderPreview()
                                    : <DocumentPreview file={resolvedFile} translate={translate} currentPage={currentPage} onPagesNumberChange={setPagesNumber}/>}
                            </div>
                        )}

                        {showSummary && (
                            <div className={classNames(panelBaseClass, 'overflow-auto', hasTrailingDivider('summary') && 'border-r border-tm-gray-200', panelWidthClass(false))}>
                                {renderSummary()}
                            </div>
                        )}

                        {showForm && (
                            <div className={classNames(panelBaseClass, 'overflow-auto', panelWidthClass(false))}>
                                {renderForm()}
                            </div>
                        )}
                    </div>

                    {(!!paginationControls || !!onSave || !!handleClose) && (
                        <footer className="shrink-0 flex h-16 border-t border-tm-gray-200 bg-popup">
                            <div className="flex-1 flex items-center justify-center px-4">
                                {paginationControls}
                            </div>

                            <div className={classNames('shrink-0 flex items-center justify-end gap-3 px-4', footerButtonSegmentWidthClass())}>
                                {!!onSave && (
                                    <button type="button" className="btn btn-primary" disabled={saveDisabled} onClick={onSave}>
                                        {saveLabel ?? translate('btn.save')}
                                    </button>
                                )}

                                {!!handleClose && (
                                    <button type="button" className="btn btn-outline" onClick={handleClose}>
                                        {cancelLabel ?? translate('btn.cancel')}
                                    </button>
                                )}
                            </div>
                        </footer>
                    )}
                </div>
            )}
        </ModalDefault>
    );
}
